import { useCallback, useEffect, useRef, useState } from "react";

import { Link, useNavigate } from "react-router-dom";

import { Edit as EditIcon } from "@mui/icons-material";
import { Button, Chip, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid, getGridSingleSelectOperators } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { ProductCategoryTypeEnum } from "@/features/product-categories";
import {
  apiClient,
  StatusEnum,
  statusColors,
  statusLabels,
  getErrorMessage,
} from "@/features/shared";
import { SearchTextField, useSnackbar } from "@/features/ui";
import { useSearchFilter, useServerPagination } from "@/hooks";

export const Products = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [products, setProducts] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const latestRequestRef = useRef(0);

  const {
    paginationModel,
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });

  const loadProducts = useCallback(async () => {
    // A status change renders before the page reset reaches the URL, so two
    // requests can overlap; only the latest one may update the grid.
    const requestId = ++latestRequestRef.current;
    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiClient.get("/products", {
        params: {
          ...paginationParams,
          ...(keyword && { keyword }),
          ...(status !== null && { status }),
          productCategoryType: ProductCategoryTypeEnum.NON_LENS,
        },
      });
      if (requestId !== latestRequestRef.current) return;
      setProducts(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      showSnackbar(getErrorMessage(error, "Gagal memuat produk"));
    } finally {
      if (requestId === latestRequestRef.current) setLoading(false);
    }
  }, [paginationParams, keyword, status, setRowCount, showSnackbar]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // The grid only holds one page, so the status filter is applied by the API.
  const handleFilterModelChange = ({ items }) => {
    const value = items.find((item) => item.field === "status")?.value;
    const nextStatus = typeof value === "boolean" ? value : null;
    if (nextStatus !== status) {
      setStatus(nextStatus);
      dataGridPaginationProps.onPaginationModelChange({
        ...paginationModel,
        page: 0,
      });
    }
  };

  const toolbarContent = hasPermission(PermissionEnum.CREATE_PRODUCTS) && (
        <Button variant="contained" component={Link} to="create">
          Tambah
        </Button>
      );

  return (
    <PageContainer maxWidth="xl" breadcrumbs={[]} slotProps={{ header: { slotProps: { toolbar: { children: toolbarContent } } } }}>
      <Stack spacing={3}>
        <Grid container sx={{ justifyContent: "flex-end" }}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari nama/sku produk"
              {...searchProps}
            />
          </Grid>
        </Grid>
        <DataGrid
          rows={products}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "name",
              headerName: "Nama",
              flex: 2,
              sortable: false,
              filterable: false,
            },
            {
              field: "skuPrefix",
              headerName: "Awalan SKU",
              flex: 1.5,
              sortable: false,
              filterable: false,
            },
            {
              field: "productCategory",
              headerName: "Kategori",
              flex: 1.5,
              sortable: false,
              filterable: false,
              valueGetter: (value) => value.name,
            },
            {
              field: "unitOfMeasurement",
              headerName: "Unit",
              flex: 1,
              sortable: false,
              filterable: false,
              valueGetter: (value) => value.name,
            },
            {
              field: "status",
              headerName: "Status",
              flex: 1,
              sortable: false,
              type: "singleSelect",
              valueOptions: [
                {
                  value: StatusEnum.ACTIVE,
                  label: statusLabels[StatusEnum.ACTIVE],
                },
                {
                  value: StatusEnum.INACTIVE,
                  label: statusLabels[StatusEnum.INACTIVE],
                },
              ],
              filterOperators: getGridSingleSelectOperators().filter(
                ({ value }) => value === "is",
              ),
              renderCell: ({ row }) => (
                <Chip
                  label={statusLabels[row.status]}
                  color={statusColors[row.status]}
                />
              ),
            },
            ...(hasPermission(PermissionEnum.UPDATE_PRODUCTS)
              ? [
                  {
                    field: "actions",
                    type: "actions",
                    headerName: "Aksi",
                    getActions: (params) => [
                      <IconButton
                        key="edit"
                        onClick={() => navigate(`${params.id}/edit`)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>,
                    ],
                  },
                ]
              : []),
          ]}
          {...dataGridPaginationProps}
          filterMode="server"
          onFilterModelChange={handleFilterModelChange}
          disableRowSelectionOnClick
        />
      </Stack>
    </PageContainer>
  );
};
